import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiChevronLeft, HiChevronRight, HiExternalLink, HiShare, HiPencil, HiArrowLeft, HiCamera, HiCheck, HiX } from 'react-icons/hi';
import ImageUploadModal from '../../components/ui/ImageUploadModal/ImageUploadModal';
import FollowersFollowingsModal from '../../components/profile/FollowersFollowingsModal';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useGeneral } from '../../contexts';
import { useChannels } from '../../hooks/useChannels';
import CommunityCard from '../../components/communities/CommunityCard/CommunityCard';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import UserAvatar from '../../components/ui/UserDisplay/UserAvatar';
import ChannelInitialIcon from '../../components/ui/ChannelInitialIcon/ChannelInitialIcon';
import SEO from '../../components/SEO/SEO';
import { generateEventUrl, extractUserIdFromProfileUrl, generateCommunityUrl, generateProfileUrl } from '../../utils/urlSlug';
import { convertFileToBase64 } from '../../utils/fileConversion';
import { generatePersonSchema } from '../../utils/seoSchemas';
import { formatEventDateRange } from '../../utils/dateFormatters';
import { EventListItem, UserCertificate } from '../../types';
import { MyTicket } from '../../types/ticket.types';
import { Channel } from '../../types/channel.types';
import Services from '../../services/Services';
import styles from './ProfilePage.module.css';

const ProfilePage: React.FC = () => {
  const { userPath } = useParams<{ userPath: string }>();
  const navigate = useNavigate();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token, user, updateUser } = auth;
  const { getTranslation, language: currentLanguage } = language;

  // Determine if this is self-profile or other-profile
  const targetUserID = useMemo(() => {
    if (userPath) {
      return extractUserIdFromProfileUrl(userPath);
    }
    return null;
  }, [userPath]);

  const isSelf = useMemo(() => {
    if (targetUserID && user?.id) {
      return targetUserID === user.id;
    }
    return false;
  }, [targetUserID, user?.id]);

  const {
    profile,
    events,
    communities,
    stats,
    isFollowing,
    followLoading,
    loading,
    error,
    followUser,
    unfollowUser,
    refetchProfile,
  } = useUserProfile({
    token,
    userID: targetUserID,
    isSelf,
    loadingKey: 'profilePage',
  });

  // Get user's joined channels to check which communities user has joined
  const { channels: myChannels } = useChannels({
    token,
    userID: user?.id,
    loadingKey: 'profileMyChannels',
  });

  // Sync profile data (avatar, name, title) to auth context so the header updates
  useEffect(() => {
    if (!isSelf || !profile) return;

    const updates: Record<string, unknown> = {};
    const profileAvatar = profile.userAvatar || undefined;
    if (profileAvatar !== user?.userAvatar) {
      updates.userAvatar = profileAvatar;
    }
    if (profile.name && profile.name !== user?.name) {
      updates.name = profile.name;
    }
    if (profile.lastname && profile.lastname !== user?.lastname) {
      updates.lastname = profile.lastname;
    }
    if (profile.email && profile.email !== user?.email) {
      updates.email = profile.email;
    }
    if (Object.keys(updates).length > 0) {
      updateUser(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, profile?.userAvatar, profile?.name, profile?.lastname, profile?.email]);

  const myChannelIDs = useMemo(() => {
    return new Set(myChannels.map(c => c.channelID));
  }, [myChannels]);

  // Map viewer's channels to get correct isAdmin status for the current viewer
  const myChannelMap = useMemo(() => {
    const map = new Map<string, Channel>();
    myChannels.forEach(c => map.set(c.channelID, c));
    return map;
  }, [myChannels]);

  // Tab state for bottom content section
  const [activeTab, setActiveTab] = useState<'events' | 'certificates' | 'tickets'>('events');

  // My Tickets state
  const [myTickets, setMyTickets] = useState<MyTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsFetched, setTicketsFetched] = useState(false);
  const [qrModalTicket, setQrModalTicket] = useState<MyTicket | null>(null);
  const [visibleTicketsCount, setVisibleTicketsCount] = useState(10);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);

  const [editTab, setEditTab] = useState<'general' | 'danger'>('general');
  const [editName, setEditName] = useState('');
  const [editLastname, setEditLastname] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalTab, setFollowersModalTab] = useState<'followers' | 'following'>('followers');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Extract certificates from profile
  const certificates = useMemo((): UserCertificate[] => {
    return profile?.certificates?.certificate || [];
  }, [profile]);

  // Community scroll state for fade gradients
  const communityScrollRef = useRef<HTMLDivElement>(null);
  const [communityScrollState, setCommunityScrollState] = useState({ canScrollLeft: false, canScrollRight: false });

  const checkScrollState = useCallback((container: HTMLDivElement | null) => {
    if (!container) {
      setCommunityScrollState({ canScrollLeft: false, canScrollRight: false });
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasScrollableContent = scrollWidth > clientWidth;
    const canScrollLeft = hasScrollableContent && scrollLeft > 5;
    const canScrollRight = hasScrollableContent && scrollLeft < scrollWidth - clientWidth - 5;
    setCommunityScrollState({ canScrollLeft, canScrollRight });
  }, []);

  useEffect(() => {
    const container = communityScrollRef.current;
    if (!container) return;

    const handleScroll = () => checkScrollState(container);
    const timeoutId = setTimeout(handleScroll, 100);

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [communities, checkScrollState]);

  const scrollByItems = useCallback((container: HTMLDivElement | null, direction: 'left' | 'right') => {
    if (!container) return;
    const itemWidth = container.querySelector('*')?.getBoundingClientRect().width || 0;
    const gap = 8;
    const scrollAmount = (itemWidth + gap) * 2;
    const currentScroll = container.scrollLeft;
    const newScroll = direction === 'right'
      ? currentScroll + scrollAmount
      : currentScroll - scrollAmount;
    container.scrollTo({ left: newScroll, behavior: 'smooth' });
  }, []);

  const handleFollowToggle = async () => {
    if (isFollowing) {
      await unfollowUser();
    } else {
      await followUser();
    }
  };

  const handleOpenFollowersModal = (tab: 'followers' | 'following') => {
    setFollowersModalTab(tab);
    setShowFollowersModal(true);
  };

  const fetchMyTickets = useCallback(async () => {
    if (!token || !user?.id || ticketsFetched) return;
    setTicketsLoading(true);
    try {
      const response = await Services.SocialHiveGetMyAllTickets(token, user.id);
      if (response.data?.status && Array.isArray(response.data.result)) {
        const sorted = [...response.data.result].sort((a, b) => {
          const createdA = Number(a.createdDateTime) || 0;
          const createdB = Number(b.createdDateTime) || 0;
          return createdB - createdA;
        });
        setMyTickets(sorted);
      }
    } catch {
      // silently fail
    } finally {
      setTicketsLoading(false);
      setTicketsFetched(true);
    }
  }, [token, user?.id, ticketsFetched]);

  const handleTicketsTabClick = useCallback(() => {
    setActiveTab('tickets');
    if (!ticketsFetched) {
      fetchMyTickets();
    }
  }, [ticketsFetched, fetchMyTickets]);

  const formatTicketDate = useCallback((ts: string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', weekday: 'long' }) => {
    if (!ts) return '-';
    const n = Number(ts);
    if (!Number.isFinite(n)) return '-';
    return new Intl.DateTimeFormat(currentLanguage, {
      timeZone: 'Europe/Istanbul',
      ...opts,
    }).format(new Date(n * 1000));
  }, [currentLanguage]);

  const formatTicketTime = useCallback((ts: string) => {
    if (!ts) return '-';
    const n = Number(ts);
    if (!Number.isFinite(n)) return '-';
    return new Intl.DateTimeFormat(currentLanguage, {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(n * 1000));
  }, [currentLanguage]);

  const handleEventClick = (event: EventListItem) => {
    const eventTitle = event.title || event.eventTitle;
    const eventID = event.eventID;
    if (eventID) {
      navigate(generateEventUrl(eventTitle, eventID));
    }
  };

  const handleCommunityClick = (community: Channel) => {
    const title = community.channelTitle || community.title;
    const channelID = community.channelID;
    if (channelID) {
      navigate(generateCommunityUrl(title, channelID));
    }
  };

  const handleJoinCommunity = async (channelID: string) => {
    if (!token) return;
    try {
      const response = await Services.SocialHiveJoinToChannel(token, user!.id, channelID);
      if (response.data.status) {
        toastSuccess(getTranslation('communities.joinedSuccess'));
      }
    } catch {
      toastError(getTranslation('common.somethingWentWrong'));
    }
  };

  const handleLeaveCommunity = async (channelID: string) => {
    if (!token) return;
    try {
      const response = await Services.SocialHiveLeaveChannel(token, user!.id, channelID);
      if (response.data.status) {
        toastSuccess(getTranslation('communities.leftSuccess'));
      }
    } catch {
      toastError(getTranslation('common.somethingWentWrong'));
    }
  };

  // Enter edit mode: populate fields from profile
  const handleEnterEditMode = useCallback(() => {
    if (profile) {
      setEditName(profile.name || '');
      setEditLastname(profile.lastname || '');
      setEditEmail(profile.email || '');
      setEditTitle(profile.title || '');
    }
    setEditTab('general');
    setIsEditMode(true);
  }, [profile]);

  const handleExitEditMode = useCallback(() => {
    setIsEditMode(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  }, []);

  const handleShareProfile = useCallback(async () => {
    const profileUrl = profile && targetUserID
      ? `${window.location.origin}${generateProfileUrl(profile.name, profile.lastname, targetUserID)}`
      : window.location.href;
    try {
      await navigator.clipboard.writeText(profileUrl);
      toastSuccess(getTranslation('common.linkCopied'));
    } catch {
      // clipboard API unavailable
    }
  }, [toastSuccess, getTranslation, profile, targetUserID]);

  // Save profile data
  const handleSaveProfile = useCallback(async () => {
    if (!token || !targetUserID || saving) {
      return;
    }

    // Email validation
    const trimmedEmail = editEmail.trim();
    if (!trimmedEmail) {
      toastError(getTranslation('profile.emailCannotBeEmpty'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toastError(getTranslation('profile.invalidEmailFormat'));
      return;
    }

    const updateData: Record<string, unknown> = {};
    if (editName !== profile?.name) updateData.name = editName;
    if (editLastname !== profile?.lastname) updateData.lastname = editLastname;
    if (editTitle !== (profile?.title || '')) updateData.title = editTitle || null;
    if (trimmedEmail !== profile?.email) updateData.email = trimmedEmail;

    if (Object.keys(updateData).length === 0) {
      toastSuccess(getTranslation('profile.profileUpdateSuccess'));
      return;
    }

    setSaving(true);
    try {
      const response = await Services.UpdateUserData(token, targetUserID, updateData);
      if (response.data.status) {
        toastSuccess(getTranslation('profile.profileUpdateSuccess'));
        refetchProfile();
      } else {
        if (
          Array.isArray(response.data.message) &&
          response.data.message.includes('email already exists')
        ) {
          toastError(getTranslation('profile.emailExist'));
        } else {
          toastError(getTranslation('profile.profileUpdateError'));
        }
      }
    } catch (err: any) {
      toastError(getTranslation('profile.profileUpdateError'));
    } finally {
      setSaving(false);
    }
  }, [token, targetUserID, saving, editName, editLastname, editTitle, editEmail, profile, toastSuccess, toastError, getTranslation, refetchProfile]);

  // Upload avatar
  const handleAvatarUpload = useCallback(async (file: File) => {
    if (!token || !targetUserID) {
      return;
    }

    setAvatarUploading(true);
    try {
      // Convert file to CoverFile format { file: base64WithoutPrefix, extension }
      const coverFile = await convertFileToBase64(file);
      const response = await Services.UpdateUserData(token, targetUserID, { userAvatar: coverFile });
      if (response.data.status) {
        toastSuccess(getTranslation('profile.profileUpdateSuccess'));
        refetchProfile();
      } else {
        toastError(getTranslation('profile.profileUpdateError'));
      }
    } catch (err: any) {
      toastError(getTranslation('profile.profileUpdateError'));
    } finally {
      setAvatarUploading(false);
      setShowAvatarModal(false);
    }
  }, [token, targetUserID, toastSuccess, toastError, getTranslation, refetchProfile]);

  // Password validation criteria
  const passwordCriteria = useMemo(() => ({
    minLength: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
  }), [newPassword]);

  const isPasswordValid = passwordCriteria.minLength && passwordCriteria.uppercase && passwordCriteria.lowercase && passwordCriteria.number;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  // Change password
  const handleChangePassword = useCallback(async () => {
    if (!token || !targetUserID || !profile?.email || passwordChanging) {
      return;
    }

    if (!isPasswordValid) {
      return;
    }
    if (!passwordsMatch) {
      setPasswordError(getTranslation('profile.passwordMismatch'));
      return;
    }

    setPasswordChanging(true);
    setPasswordError(null);

    try {
      // Step 1: Verify current password via UserLogin
      const loginResponse = await Services.UserLogin(token, profile.email, currentPassword);
      if (!loginResponse.data.status) {
        setPasswordError(getTranslation('profile.currentPasswordWrong'));
        setPasswordChanging(false);
        return;
      }

      // Step 2: Update password
      const updateResponse = await Services.UpdateUserData(token, targetUserID, { password: newPassword });
      if (updateResponse.data.status) {
        toastSuccess(getTranslation('profile.passwordChangeSuccess'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError(null);
      } else {
        toastError(getTranslation('profile.passwordChangeError'));
      }
    } catch (err: any) {
      setPasswordError(getTranslation('profile.currentPasswordWrong'));
    } finally {
      setPasswordChanging(false);
    }
  }, [token, targetUserID, profile?.email, passwordChanging, isPasswordValid, passwordsMatch, currentPassword, newPassword, getTranslation, toastSuccess, toastError]);

  // Loading state
  if (loading) {
    return (
      <>
        <SEO title="Loading Profile" noindex lang={currentLanguage} />
        <div className={styles.profilePage}>
          {/* Left skeleton */}
          <div className={styles.mainContent}>
            {/* Communities skeleton */}
            <div className={styles.communitiesCard}>
              <LoadingSkeleton width={120} height={20} />
              <div style={{ display: 'flex', gap: 8 }}>
                <LoadingSkeleton width={280} height={160} />
                <LoadingSkeleton width={280} height={160} />
                <LoadingSkeleton width={280} height={160} />
              </div>
            </div>
            {/* Events skeleton */}
            <div className={styles.eventsListContainer}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <LoadingSkeleton width={80} height={36} />
                <LoadingSkeleton width={100} height={36} />
              </div>
              <div style={{ height: 1, background: 'var(--color-border-gray)', marginBottom: 8 }} />
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0', borderBottom: i < 4 ? '1px solid var(--color-border-gray)' : 'none' }}>
                  <LoadingSkeleton width={120} height={68} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <LoadingSkeleton width="80%" height={16} />
                    <LoadingSkeleton width="40%" height={12} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Sidebar skeleton */}
          <div className={styles.sidebar}>
            <div className={styles.userInfoCard} style={{ alignItems: 'center' }}>
              <LoadingSkeleton variant="circular" width={120} height={120} />
              <LoadingSkeleton width={180} height={22} />
              <LoadingSkeleton width={120} height={14} />
              <div style={{ display: 'flex', gap: 24, justifyContent: 'center', width: '100%', marginTop: 4 }}>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <LoadingSkeleton width={28} height={20} />
                    <LoadingSkeleton width={52} height={12} />
                  </div>
                ))}
              </div>
              <LoadingSkeleton width="100%" height={44} />
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <>
        <SEO
          title={getTranslation('profile.profileNotFound') || 'Profile Not Found'}
          noindex
          lang={currentLanguage}
        />
        <div className={styles.errorContainer}>
          <EmptyState
            title={getTranslation('profile.profileNotFound')}
            description={error || undefined}
          />
        </div>
      </>
    );
  }

  const fullName = `${profile.name} ${profile.lastname}`.trim();
  const canonicalUrl = targetUserID
    ? `${window.location.origin}${generateProfileUrl(profile.name, profile.lastname, targetUserID)}`
    : undefined;
  const seoDescription = profile.title
    ? `${fullName} – ${profile.title}`
    : fullName;
  const structuredData = generatePersonSchema({
    name: fullName,
    url: canonicalUrl || window.location.href,
    imageUrl: profile.userAvatar || undefined,
    jobTitle: profile.title || undefined,
  });

  return (
    <div className={styles.profilePage}>
      <SEO
        title={fullName}
        description={seoDescription}
        image={profile.userAvatar || undefined}
        url={canonicalUrl}
        type="website"
        structuredData={structuredData}
        lang={currentLanguage}
      />
      {/* Left Content */}
      <div className={styles.mainContent}>
        {isEditMode ? (
          <div className={styles.editContainer}>
            {/* Edit Header */}
            <div className={styles.editHeader}>
              <button className={styles.backButton} onClick={handleExitEditMode}>
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiArrowLeft />
              </button>
              <h2 className={styles.editTitle}>{getTranslation('profile.editProfile')}</h2>
            </div>

            {/* Edit Tabs */}
            <div className={styles.editTabs}>
              <button
                className={`${styles.tabButton} ${editTab === 'general' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => setEditTab('general')}
              >
                {getTranslation('profile.generalInfo')}
              </button>
              <button
                className={`${styles.tabButton} ${editTab === 'danger' ? styles.tabButtonActiveDanger : styles.tabButtonInactiveDanger}`}
                onClick={() => setEditTab('danger')}
              >
                {getTranslation('profile.dangerZone')}
              </button>
            </div>

            <div className={styles.tabsSeparator} />

            {/* General Info Tab */}
            {editTab === 'general' && (
              <div className={styles.editForm}>
                {/* Avatar Edit */}
                <div className={styles.avatarEditWrapper}>
                  <button
                    className={styles.avatarEditButton}
                    onClick={() => setShowAvatarModal(true)}
                    disabled={avatarUploading}
                  >
                    <UserAvatar
                      avatarUrl={profile.userAvatar}
                      name={fullName}
                      size={120}
                    />
                    <div className={styles.avatarOverlay}>
                      {/* @ts-ignore - react-icons compatibility with React 19 */}
                      <HiCamera />
                    </div>
                  </button>
                  <button
                    className={styles.changePhotoText}
                    onClick={() => setShowAvatarModal(true)}
                    disabled={avatarUploading}
                  >
                    {avatarUploading ? getTranslation('common.saving') : getTranslation('profile.changePhoto')}
                  </button>
                </div>

                {/* Name */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{getTranslation('profile.nameField')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                </div>

                {/* Surname */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{getTranslation('profile.surnameField')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={editLastname}
                    onChange={(e) => setEditLastname(e.target.value)}
                  />
                </div>

                {/* Title */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{getTranslation('profile.titleField')}</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder={getTranslation('profile.titlePlaceholder')}
                  />
                </div>

                {/* Email (read-only) */}
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{getTranslation('profile.emailField')}</label>
                  <input
                    type="email"
                    className={styles.formInput}
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                  />
                </div>

                {/* Save Button */}
                <div className={styles.saveButton}>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    {saving ? getTranslation('common.saving') : getTranslation('profile.saveChanges')}
                  </Button>
                </div>
              </div>
            )}

            {/* Danger Zone Tab */}
            {editTab === 'danger' && (
              <div className={styles.dangerZoneSection}>
                <h3 className={styles.dangerZoneHeader}>{getTranslation('profile.changePassword')}</h3>
                <div className={styles.passwordForm}>
                  {/* Current Password */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{getTranslation('profile.currentPassword')}</label>
                    <input
                      type="password"
                      className={styles.formInput}
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        setPasswordError(null);
                      }}
                      placeholder="••••••••"
                    />
                  </div>

                  {/* New Password */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{getTranslation('profile.newPassword')}</label>
                    <input
                      type="password"
                      className={styles.formInput}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>

                  {/* Password Criteria */}
                  {newPassword.length > 0 && (
                    <div className={styles.passwordCriteria}>
                      <p className={styles.passwordCriteriaTitle}>{getTranslation('profile.passwordCriteria')}</p>
                      <div className={`${styles.criteriaItem} ${passwordCriteria.minLength ? styles.criteriaMet : styles.criteriaUnmet}`}>
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        {passwordCriteria.minLength ? <HiCheck /> : <HiX />}
                        {getTranslation('profile.passwordMinLength')}
                      </div>
                      <div className={`${styles.criteriaItem} ${passwordCriteria.uppercase ? styles.criteriaMet : styles.criteriaUnmet}`}>
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        {passwordCriteria.uppercase ? <HiCheck /> : <HiX />}
                        {getTranslation('profile.passwordUppercase')}
                      </div>
                      <div className={`${styles.criteriaItem} ${passwordCriteria.lowercase ? styles.criteriaMet : styles.criteriaUnmet}`}>
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        {passwordCriteria.lowercase ? <HiCheck /> : <HiX />}
                        {getTranslation('profile.passwordLowercase')}
                      </div>
                      <div className={`${styles.criteriaItem} ${passwordCriteria.number ? styles.criteriaMet : styles.criteriaUnmet}`}>
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        {passwordCriteria.number ? <HiCheck /> : <HiX />}
                        {getTranslation('profile.passwordNumber')}
                      </div>
                    </div>
                  )}

                  {/* Confirm Password */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>{getTranslation('profile.confirmPassword')}</label>
                    <input
                      type="password"
                      className={styles.formInput}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>

                  {/* Password mismatch warning */}
                  {confirmPassword.length > 0 && !passwordsMatch && (
                    <p className={styles.passwordError}>{getTranslation('profile.passwordMismatch')}</p>
                  )}

                  {/* Server error */}
                  {passwordError && (
                    <p className={styles.passwordError}>{passwordError}</p>
                  )}

                  {/* Change Password Button */}
                  <div className={styles.saveButton}>
                    <Button
                      variant="primary"
                      size="medium"
                      onClick={handleChangePassword}
                      disabled={passwordChanging || !isPasswordValid || !passwordsMatch || !currentPassword}
                    >
                      {passwordChanging ? getTranslation('common.saving') : getTranslation('profile.changePassword')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
        <div className={styles.profileContent}>
        {/* Communities Section */}
        {communities.length > 0 && (
          <div className={styles.communitiesCard}>
            <h3 className={styles.sectionTitle}>
              {getTranslation('profile.communities')}
            </h3>
            <div className={styles.scrollableContainer}>
              <div className={`${styles.scrollGradientLeft} ${communityScrollState.canScrollLeft ? styles.visible : ''}`} />
              <div className={`${styles.scrollGradientRight} ${communityScrollState.canScrollRight ? styles.visible : ''}`} />
              {communityScrollState.canScrollLeft && (
                <button
                  className={`${styles.chevronButton} ${styles.chevronLeft}`}
                  onClick={() => scrollByItems(communityScrollRef.current, 'left')}
                  aria-label="Scroll left"
                >
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiChevronLeft />
                </button>
              )}
              {communityScrollState.canScrollRight && (
                <button
                  className={`${styles.chevronButton} ${styles.chevronRight}`}
                  onClick={() => scrollByItems(communityScrollRef.current, 'right')}
                  aria-label="Scroll right"
                >
                  {/* @ts-ignore - react-icons compatibility with React 19 */}
                  <HiChevronRight />
                </button>
              )}
              <div className={styles.communityScrollContainer} ref={communityScrollRef}>
                {communities.map((community) => {
                  const channelID = community.channelID;
                  const isJoined = myChannelIDs.has(channelID);
                  // Use the viewer's admin status, not the viewed profile's
                  const viewerChannel = myChannelMap.get(channelID);
                  const viewerIsAdmin = viewerChannel?.isAdmin || "0";
                  const communityForCard = { ...community, isAdmin: viewerIsAdmin };
                  return (
                    <CommunityCard
                      key={channelID}
                      community={communityForCard as any}
                      isJoined={isJoined}
                      onJoin={handleJoinCommunity}
                      onLeave={handleLeaveCommunity}
                      onClick={() => handleCommunityClick(community)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {communities.length === 0 && !loading && (
          <div className={styles.communitiesCard}>
            <h3 className={styles.sectionTitle}>
              {getTranslation('profile.communities')}
            </h3>
            <EmptyState title={getTranslation('profile.noCommunities')} />
          </div>
        )}

        {/* Events & Certificates Section */}
        <div className={styles.eventsSection}>
          <div className={styles.eventsListContainer}>
            <div className={styles.tabsHeader}>
              <button
                className={`${styles.tabButton} ${activeTab === 'events' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => setActiveTab('events')}
              >
                {getTranslation('profile.eventsTab')}
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'certificates' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                onClick={() => setActiveTab('certificates')}
              >
                {getTranslation('profile.certificatesTab')}
              </button>
              {isSelf && (
                <button
                  className={`${styles.tabButton} ${activeTab === 'tickets' ? styles.tabButtonActive : styles.tabButtonInactive}`}
                  onClick={handleTicketsTabClick}
                >
                  {getTranslation('profile.myTicketsTab')}
                </button>
              )}
            </div>
            <div className={styles.tabsSeparator} />

            {/* Events Tab Content */}
            {activeTab === 'events' && (
              <div className={styles.eventsList}>
                {events.length > 0 ? (
                  events.filter((e) => e.isPublic !== "3").map((event) => {
                    const eventTitle = event.title || event.eventTitle;
                    const coverImage = event.cover || event.bannerImage || event.image || null;
                    const eventDate = formatEventDateRange(event.eventStartDateTime, event.eventEndDateTime, currentLanguage);

                    return (
                      <div
                        key={event.eventID}
                        className={styles.eventItemRow}
                        onClick={() => handleEventClick(event)}
                      >
                        {coverImage && (
                          <div className={styles.eventCover}>
                            <img
                              src={coverImage}
                              alt={eventTitle}
                              className={styles.eventCoverImage}
                              onError={(e) => {
                                (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className={styles.eventItemContent}>
                          <p className={styles.eventItemText}>
                            {(() => {
                              const template = getTranslation('profile.joinedEvent');
                              const parts = template.split('{title}');
                              return (
                                <>
                                  {parts[0]}<span className={styles.eventName}>{eventTitle}</span>{parts[1] || ''}
                                </>
                              );
                            })()}
                          </p>
                          <p className={styles.eventItemDate}>
                            {eventDate}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '24px 0' }}>
                    <EmptyState title={getTranslation('profile.noEvents')} />
                  </div>
                )}
              </div>
            )}

            {/* Certificates Tab Content */}
            {activeTab === 'certificates' && (
              <div className={styles.eventsList}>
                {certificates.length > 0 ? (
                  certificates.map((cert, index) => {
                    const communityName = cert.channelDetails?.title || cert.channelDetails?.channelTitle || '';
                    const communityLogo = cert.channelDetails?.logo || null;
                    const certUrl = cert.url || null;

                    const certImage = cert.certificate?.url || null;

                    return (
                      <div
                        key={`cert-${index}`}
                        className={styles.eventItemRow}
                        onClick={() => {
                          if (certUrl) {
                            window.open(certUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        style={{ cursor: certUrl ? 'pointer' : 'default' }}
                      >
                        {certImage && (
                          <div className={styles.certCover}>
                            <img
                              src={certImage}
                              alt={cert.certificateProgramTitle}
                              className={styles.eventCoverImage}
                              onError={(e) => {
                                (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className={styles.eventItemContent}>
                          <p className={styles.eventItemText}>
                            <span className={styles.eventName}>{cert.certificateProgramTitle}</span>
                          </p>
                          <p className={styles.certMeta}>
                            {communityName && (
                              <span className={styles.certChannel}>
                                {communityLogo ? (
                                  <img
                                    src={communityLogo}
                                    alt={communityName}
                                    className={styles.certChannelIcon}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <ChannelInitialIcon
                                    channelName={communityName}
                                    size={16}
                                  />
                                )}
                                <span>{communityName}</span>
                              </span>
                            )}
                            {communityName && cert.certificateProgramDuration && <span>·</span>}
                            {cert.certificateProgramDuration && (
                              <span>{cert.certificateProgramDuration} {getTranslation(`profile.duration_${cert.certificateProgramDurationUnit}`)}</span>
                            )}
                            {(communityName || cert.certificateProgramDuration) && cert.certificateProgramTotalHour && <span>·</span>}
                            {cert.certificateProgramTotalHour && (
                              <span>{cert.certificateProgramTotalHour} {getTranslation('profile.hours')}</span>
                            )}
                          </p>
                        </div>
                        {certUrl && (
                          <span className={styles.certLinkIcon}>
                            {/* @ts-ignore - react-icons compatibility with React 19 */}
                            <HiExternalLink size={18} />
                          </span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '24px 0' }}>
                    <EmptyState title={getTranslation('profile.noCertificates')} />
                  </div>
                )}
              </div>
            )}

            {/* My Tickets Tab Content */}
            {activeTab === 'tickets' && isSelf && (
              <div className={styles.ticketsList}>
                {ticketsLoading ? (
                  <div className={styles.ticketsLoading}>
                    <div className={styles.ticketSkeleton} />
                    <div className={styles.ticketSkeleton} />
                  </div>
                ) : myTickets.length > 0 ? (
                  <>
                    {myTickets.slice(0, visibleTicketsCount).map((ticket) => {
                      const isUsed = ticket.isUsed === '1';
                      return (
                        <div
                          key={ticket.id || ticket.ticketNo}
                          className={styles.ticketCard}
                          onClick={() => {
                            if (ticket.eventID) {
                              navigate(generateEventUrl(ticket.eventTitle, ticket.eventID));
                            }
                          }}
                        >
                          {ticket.cover && (
                            <div className={styles.ticketCardBanner}>
                              <img
                                src={ticket.cover}
                                alt={ticket.eventTitle || ''}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                                }}
                              />
                            </div>
                          )}

                          <div className={styles.ticketCardInfo}>
                            <div className={styles.ticketCardColumn}>
                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketEvent')}</span>
                              <span className={styles.ticketCardValue}>{ticket.eventTitle || '-'}</span>

                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketTimeRange')}</span>
                              <span className={styles.ticketCardValue}>
                                {`${formatTicketTime(ticket.eventStartDateTime)} - ${formatTicketTime(ticket.eventEndDateTime)}`}
                              </span>

                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketPurchaseDate')}</span>
                              <span className={styles.ticketCardValue}>
                                {formatTicketDate(ticket.paymentDateTime)}
                              </span>
                            </div>

                            <div className={styles.ticketCardColumn}>
                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketEventDate')}</span>
                              <span className={styles.ticketCardValue}>
                                {formatTicketDate(ticket.eventStartDateTime)}
                              </span>

                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketEventLocation')}</span>
                              <span className={styles.ticketCardValue}>{ticket.locationAddress || '-'}</span>

                              <span className={styles.ticketCardLabel}>{getTranslation('profile.ticketType')}</span>
                              <span className={styles.ticketCardValue}>{ticket.title || '-'}</span>
                            </div>
                          </div>

                          {ticket.ticketQRCode && (
                            <div
                              className={`${styles.ticketCardQR} ${isUsed ? styles.ticketUsedShift : ''}`}
                              onClick={(e) => { e.stopPropagation(); setQrModalTicket(ticket); }}
                            >
                              <img src={ticket.ticketQRCode} alt="QR Code" />
                              {isUsed && (
                                <div className={styles.ticketUsedWatermark}>USED</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {visibleTicketsCount < myTickets.length && (
                      <button
                        type="button"
                        className={styles.showMoreButton}
                        onClick={() => setVisibleTicketsCount(prev => prev + 10)}
                      >
                        {getTranslation('common.showMore')} ({myTickets.length - visibleTicketsCount})
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{ padding: '24px 0' }}>
                    <EmptyState title={getTranslation('profile.noTickets')} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
        )}
      </div>

      {/* QR Code Modal */}
      {qrModalTicket && (
        <div className={styles.qrModalOverlay} onClick={() => setQrModalTicket(null)}>
          <div className={styles.qrModalContent} onClick={(e) => e.stopPropagation()}>
            <img src={qrModalTicket.ticketQRCode} alt="QR Code" />
          </div>
        </div>
      )}

      {/* Right Sidebar */}
      <div className={styles.sidebar}>
        <div className={styles.userInfoCard}>
          <div className={styles.actionButtons}>
            {isSelf && (
              <button className={styles.actionButton} onClick={handleEnterEditMode} aria-label={getTranslation('profile.editProfile')}>
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                <HiPencil size={18} />
              </button>
            )}
            <button className={styles.actionButton} onClick={handleShareProfile} aria-label={getTranslation('profile.shareProfile')}>
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiShare size={18} />
            </button>
          </div>

          {/* Avatar */}
          <UserAvatar
            avatarUrl={profile.userAvatar}
            name={fullName}
            size={120}
          />

          {/* Name */}
          <h2 className={styles.userName}>{fullName}</h2>

          {/* Title */}
          {profile.title && (
            <p className={styles.userTitle}>{profile.title}</p>
          )}

          {/* Stats Row */}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{stats.eventsCount}</span>
              <span className={styles.statLabel}>{getTranslation('profile.events')}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statValue}>{stats.communitiesCount}</span>
              <span className={styles.statLabel}>{getTranslation('profile.communities')}</span>
            </div>
            <div
              className={`${styles.statItem} ${styles.statItemClickable}`}
              onClick={() => handleOpenFollowersModal('followers')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleOpenFollowersModal('followers')}
            >
              <span className={styles.statValue}>{stats.followerCount}</span>
              <span className={styles.statLabel}>{getTranslation('profile.followers')}</span>
            </div>
            <div
              className={`${styles.statItem} ${styles.statItemClickable}`}
              onClick={() => handleOpenFollowersModal('following')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleOpenFollowersModal('following')}
            >
              <span className={styles.statValue}>{stats.followingCount}</span>
              <span className={styles.statLabel}>{getTranslation('profile.following')}</span>
            </div>
          </div>

          {/* Follow Button (only for other users) */}
          {!isSelf && (
            <Button
              variant={isFollowing ? 'secondary' : 'primary'}
              size="large"
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={styles.followButton}
            >
              {isFollowing
                ? getTranslation('profile.unfollow')
                : getTranslation('profile.follow')
              }
            </Button>
          )}
        </div>
      </div>

      {/* Avatar Upload Modal */}
      <ImageUploadModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onUpload={handleAvatarUpload}
        title={getTranslation('profile.changePhoto')}
        aspectRatio="1:1"
        recommendedSize="400x400"
        getTranslation={getTranslation}
      />

      {/* Followers/Followings Modal */}
      <FollowersFollowingsModal
        isOpen={showFollowersModal}
        onClose={() => setShowFollowersModal(false)}
        initialTab={followersModalTab}
        token={token || ''}
        userID={targetUserID || undefined}
        currentUserID={user?.id}
        followerCount={stats.followerCount}
        followingCount={stats.followingCount}
        getTranslation={getTranslation}
      />
    </div>
  );
};

export default ProfilePage;
